import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, HeartHandshake, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/gallery", label: "Gallery" },
  { to: "/events", label: "Events" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-accent" : "text-foreground/80 hover:text-accent"}`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors ${
        scrolled ? "backdrop-blur-xl bg-background/85 border-border" : "bg-background/60 backdrop-blur-md border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-10 h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <HeartHandshake className="h-6 w-6 text-accent" strokeWidth={1.5} />
          <span className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-foreground">
            PhysioAbhi
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:block">
          <Button asChild data-testid="nav-donate-button" className="rounded-none bg-accent text-accent-foreground hover:bg-[hsl(var(--accent))]/90 px-6 group">
            <Link to="/donate">
              Donate Now <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <button data-testid="nav-mobile-menu-button" aria-label="Open menu">
              <Menu className="h-6 w-6 text-foreground" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-background border-border rounded-none w-[80%]">
            <SheetTitle className="font-heading text-2xl">Menu</SheetTitle>
            <div className="flex flex-col gap-6 mt-8">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="text-lg font-heading text-foreground hover:text-accent"
                >
                  {l.label}
                </NavLink>
              ))}
              <Button asChild data-testid="nav-mobile-donate-button" className="rounded-none bg-accent text-accent-foreground mt-2">
                <Link to="/donate" onClick={() => setOpen(false)}>Donate Now</Link>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};
